import { computed } from 'vue'
import { QDialog } from 'quasar'

/**
 * The plumbing every Quasar-plugin dialog in this codebase repeats: resolve the inner `q-dialog`
 * from a `$refs` name, open it, close it, and re-emit its `hide` so the plugin can tear the
 * component down.
 *
 * 47 components declare an `onDialogHide`, and the four members below are identical in all of them.
 * The two that are *not* identical are `cancel` and `confirm` — most dialogs cancel by closing and
 * confirm by emitting `ok` and closing, but several differ: `TermlyDialog` emits `ok` when cancelled,
 * `RejectApplicationDialog` emits its reason with `ok` from a separate method and leaves `confirm`
 * empty. So those two are supplied as defaults and a caller that differs simply overrides them:
 *
 *   const controls = useDialogControls({ vm, dialogRef: 'myDialog', emit })
 *   ...
 *   return { ...controls, cancel: () => { emit('ok'); controls.hide() } }
 *
 * `show`, `hide`, `onDialogHide` and `confirm` have to end up on the instance for `$q.dialog()` to
 * drive the component, which is why the whole bag is spread into what `setup` returns.
 */
export interface DialogControlsContext {
  /** The `ref="..."` on the q-dialog in the caller's own template. */
  dialogRef: string
  vm: any
  emit: (event: string, ...args: any[]) => void
}

export function useDialogControls (context: DialogControlsContext) {
  const { vm, emit } = context

  /**
   * A computed, as the class getters this replaces also were — `$refs` is not reactive, so it
   * resolves once, on the first read after the dialog has rendered. A spec has to seed the ref
   * before that first read or it caches undefined.
   */
  const dialog = computed(() => vm.$refs[context.dialogRef] as QDialog)

  const show = () => {
    dialog.value.show()
  }

  const hide = () => {
    dialog.value.hide()
  }

  const onDialogHide = () => {
    emit('hide')
  }

  /** Default: close and say nothing. Override where dismissing means something. */
  const cancel = () => {
    hide()
  }

  /** Default: tell the opener it succeeded, then close. */
  const confirm = async () => {
    emit('ok')
    hide()
  }

  return { dialog, show, hide, onDialogHide, cancel, confirm }
}
